/**
 * Centralized error handling utilities.
 */

/**
 * Extract a user-friendly error message from an API error.
 */
export const getErrorMessage = (error) => {
  if (!error) return 'Unknown error';

  // Axios error with response
  if (error.response) {
    const { status, data } = error.response;
    const rawDetail = data?.detail || data?.message || data?.error;

    // For 500s, log full detail to console but show a clean message to the
    // user.  Pydantic validation errors produce an array of error objects
    // that should never be rendered as page content or toast text.
    if (status >= 500) {
      if (rawDetail) {
        // eslint-disable-next-line no-console
        console.error(`Server error (HTTP ${status}) detail:`, rawDetail);
      }
      // A structured server error (FastAPI HTTPException with a dict detail that
      // names a pipeline stage) IS safe and useful to show: surface the message,
      // the stage, and the offending document instead of a bare code.  Pydantic
      // validation errors are arrays and are deliberately NOT rendered here.
      if (rawDetail && typeof rawDetail === 'object' && !Array.isArray(rawDetail) && rawDetail.stage) {
        const parts = [rawDetail.message || rawDetail.error || 'Server error'];
        const meta = [];
        if (rawDetail.stage) meta.push(`stage: ${rawDetail.stage}`);
        if (rawDetail.doc_id) meta.push(`doc: ${rawDetail.doc_id}`);
        return meta.length ? `${parts[0]} (${meta.join(', ')})` : parts[0];
      }
      // A plain string detail (a raised HTTPException(status_code=503,
      // detail="...") or a proxy/upstream error body) is real, useful
      // context for the operator -- never worth discarding down to a bare
      // "Server error (HTTP 503)" (CLAUDE.md section 6: hard failures loud
      // and visible, never silent). A Pydantic validation-error array or an
      // unrecognized object shape still falls through to the bare code.
      if (typeof rawDetail === 'string' && rawDetail.trim()) {
        return `Server error (HTTP ${status}): ${rawDetail}`;
      }
      return `Server error (HTTP ${status})`;
    }

    // If detail is an object (e.g. FastAPI HTTPException with dict detail),
    // extract the reason string or stringify it so it never renders as [object Object].
    const detail = rawDetail && typeof rawDetail === 'object'
      ? (rawDetail.reason || rawDetail.message || JSON.stringify(rawDetail))
      : rawDetail;

    if (status === 400) return detail || 'Invalid request';
    if (status === 401) return 'Session expired - please log in again';
    if (status === 403) return 'You do not have permission for this action';
    if (status === 404) return detail || 'Resource not found';
    if (status === 409) return detail || 'Conflict - resource was modified by another user';
    if (status === 422) return detail || 'Invalid input data';
    if (status === 429) return 'Too many requests - please wait a moment';
  }

  // Network error
  if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
    return 'Network error - check your connection';
  }

  // Timeout
  if (error.code === 'ECONNABORTED') {
    return 'Request timed out - please try again';
  }

  return error.message || 'An unexpected error occurred';
};

/**
 * True for the Xero invoice sync "Run Now" 409 shape (routes/xero.py::
 * sync_xero_data): a genuinely in-progress run, not a stale/orphaned one
 * (those self-heal server-side, see services/xero_invoice_sync.py's
 * _active_run). Same "fixed status string, dedicated UI state" convention
 * as isGoogleReauthRequiredError.
 */
export const isXeroSyncInProgressError = (err) =>
  err?.response?.status === 409 && err.response?.data?.detail?.status === 'xero_sync_in_progress';

/** Human-readable "run in progress, started HH:MM" message for
 * isXeroSyncInProgressError's error shape -- so a repeated Run Now click
 * tells the operator a real run is live instead of silently re-rendering
 * whatever the panel already had on screen. */
export const getXeroSyncInProgressMessage = (err) => {
  const detail = err?.response?.data?.detail || {};
  if (!detail.started_at) return 'A Xero invoice sync is already in progress.';
  const started = new Date(detail.started_at);
  const time = Number.isNaN(started.getTime())
    ? detail.started_at
    : started.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `A Xero invoice sync is already in progress, started ${time}.`;
};

/**
 * True for the Gmail scan endpoints' clean 409 shape (routes/
 * google_integrations.py::_reauth_required_detail): a connected Google
 * account whose token is dead or whose scope no longer covers the scan
 * path. Same "fixed status string, dedicated UI state" convention as
 * XeroBankFeedConfigureModal's isXeroNotConnectedError.
 */
export const isGoogleReauthRequiredError = (err) =>
  err?.response?.status === 409 && err.response?.data?.detail?.status === 'google_reauth_required';

const GOOGLE_REAUTH_REASON_LABELS = {
  token_refresh_failed: 'Google sign-in expired',
  insufficient_scopes: 'Google connection is missing required Gmail/Drive permissions',
};

/** Human-readable message for isGoogleReauthRequiredError's error shape. */
export const getGoogleReauthMessage = (err) => {
  const detail = err?.response?.data?.detail || {};
  const label = GOOGLE_REAUTH_REASON_LABELS[detail.reason] || 'Google reconnect required';
  return `${label} — click Reconnect to restore Gmail scanning.`;
};

/**
 * True for the Gmail scan endpoints' google_api_not_configured 409 shape
 * (routes/google_integrations.py::_api_not_configured_detail): the Gmail
 * API is disabled on the Google Cloud project behind this account's
 * OAuth client. Deliberately distinct from isGoogleReauthRequiredError --
 * reconnecting cannot fix this, so it must not render the Reconnect CTA.
 */
export const isGoogleApiNotConfiguredError = (err) =>
  err?.response?.status === 409 && err.response?.data?.detail?.status === 'google_api_not_configured';

/** Human-readable message for isGoogleApiNotConfiguredError's error shape. */
export const getGoogleApiNotConfiguredMessage = (err) => {
  const detail = err?.response?.data?.detail || {};
  const help = detail.extended_help_url ? ` (${detail.extended_help_url})` : '';
  return `${detail.message || 'Gmail API is disabled for this account\'s Google Cloud project.'}${help}`;
};

const SCOPE_SHORT_NAMES = {
  'https://www.googleapis.com/auth/gmail.modify': 'Gmail',
  'https://www.googleapis.com/auth/drive': 'Drive',
};

/** Human-readable label for a scan-error code (last_scan_error), the
 * value the Integrations card persists and renders. Every code this
 * codebase actually produces (services/gmail.py::classify_gmail_scan_
 * exception, GoogleReauthRequiredError.reason values, and the manual
 * scan route's generic scan_failed:<ExceptionType> fallback) must have
 * an entry here or fall through to a labelled-but-honest default --
 * never render the bare code to the operator. */
export const describeGoogleScanError = (code, missingScopes = []) => {
  if (!code) return null;
  if (code === 'gmail_api_not_configured') {
    return {
      title: 'Gmail API is disabled for this account\'s Google Cloud project',
      detail: 'An admin must enable the Gmail API for the project behind this account\'s OAuth client in Google Cloud Console (APIs & Services → Library → Gmail API → Enable), then Reconnect.',
      reconnectHelps: false,
    };
  }
  if (code === 'insufficient_scopes') {
    const missing = missingScopes.map(s => SCOPE_SHORT_NAMES[s] || s).join(', ');
    return {
      title: missing ? `Granted scopes are missing: ${missing}` : 'Granted scopes do not cover Gmail scanning',
      detail: 'Reconnect and grant every checkbox on Google\'s consent screen (Gmail and Drive).',
      reconnectHelps: true,
    };
  }
  if (code === 'token_refresh_failed') {
    return {
      title: 'Google sign-in expired',
      detail: 'The refresh token is dead or revoked. Reconnect to restore scanning.',
      reconnectHelps: true,
    };
  }
  if (code === 'google_api_error') {
    return {
      title: 'Google API error during the last scan',
      detail: 'Not a permissions issue — check server logs for the account. May be transient; try Scan Invoices again before reconnecting.',
      reconnectHelps: false,
    };
  }
  if (code.startsWith('scan_failed:')) {
    return {
      title: `Scan failed (${code.slice('scan_failed:'.length)})`,
      detail: 'Check server logs for this account. May be transient.',
      reconnectHelps: false,
    };
  }
  return {
    title: `Scan failed (${code})`,
    detail: 'Unrecognized error code — check server logs for this account.',
    reconnectHelps: false,
  };
};

/** "Granted: Gmail, Drive" / "Granted: Drive — missing: Gmail" / unknown,
 * for the always-visible scopes summary line on every connected Google
 * account (healthy ones too), so a account_2-shaped mystery is readable
 * at a glance instead of requiring the diagnostics panel. */
export const describeGoogleScopesSummary = (account) => {
  if (!account?.scopes_known) {
    return 'Scopes: unknown (reconnect to record what was granted)';
  }
  const granted = (account.granted_scopes || []).map(s => SCOPE_SHORT_NAMES[s] || s);
  const missing = (account.missing_scan_scopes || []).map(s => SCOPE_SHORT_NAMES[s] || s);
  if (missing.length === 0) {
    return `Granted: ${granted.join(', ') || 'none'}`;
  }
  return `Granted: ${granted.join(', ') || 'none'} — missing: ${missing.join(', ')}`;
};
